import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  startAuthentication,
  startRegistration,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import type { AuthUser, PasskeyRow } from '@seenaly/shared';
import { api, apiFetch } from '@/lib/api';

/**
 * Fingerprint / face sign-in (docs/spec.md §14 round 4, G2).
 *
 * Each flow is two round trips: the server issues a challenge, the browser
 * asks the device to sign it, and the server verifies the answer. The
 * `challengeId` ties the two halves together so a challenge is spent once.
 */

export const passkeyKeys = {
  all: ['passkeys'] as const,
};

/** Whether this browser can do platform (fingerprint/face) authentication at all. */
export function passkeysSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    window.isSecureContext &&
    typeof window.PublicKeyCredential !== 'undefined'
  );
}

export function usePasskeys() {
  return useQuery({
    queryKey: passkeyKeys.all,
    queryFn: () => apiFetch<PasskeyRow[]>('/auth/passkeys'),
  });
}

export function useDeletePasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<{ id: string }>(`/auth/passkeys/${encodeURIComponent(id)}`),
    onSuccess: () => client.invalidateQueries({ queryKey: passkeyKeys.all }),
  });
}

export function useRegisterPasskey() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (label: string) => {
      const started = await api.post<{
        options: PublicKeyCredentialCreationOptionsJSON;
        challengeId: string;
      }>('/auth/passkeys/register/start', {});

      // Raises the platform's own prompt — the fingerprint is read by the
      // device here and never reaches this code or the server.
      const response = await startRegistration({ optionsJSON: started.options });

      return api.post<{ ok: boolean }>('/auth/passkeys/register/finish', {
        challengeId: started.challengeId,
        response,
        label,
      });
    },
    onSuccess: () => client.invalidateQueries({ queryKey: passkeyKeys.all }),
  });
}

/**
 * Signing in with a finger. Returns the same `AuthUser` the password login
 * does, so the caller feeds it to the auth context identically.
 */
export async function loginWithPasskey(): Promise<AuthUser> {
  const started = await api.post<{
    options: PublicKeyCredentialRequestOptionsJSON;
    challengeId: string;
  }>('/auth/passkeys/login/start', {});

  const response = await startAuthentication({ optionsJSON: started.options });

  return api.post<AuthUser>('/auth/passkeys/login/finish', {
    challengeId: started.challengeId,
    response,
  });
}
